import DownloadIcon from "@mui/icons-material/Download";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { Alert, Dialog } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useFileTransfer } from "@/providers/FileTransfer";
import { AddToCollection } from "@/shared/components/collections";
import {
  ResourceView,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { useCreateExportJobMutation } from "@/shared/domain/exportJobs/hooks";
import { type SessionFile } from "@/shared/domain/sessions";
import { useSessionFiles } from "@/shared/domain/sessions/hooks";
import {
  buildColumnsForPreferences,
  cellStyles,
  colId,
  ColumnVisibility,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  EmptyCell,
  renderDashOr,
  useTable,
  type ColDef,
  type ColumnPickerOption,
  type ICellRendererParams,
  type ValueFormatterParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { usePopupState } from "@/shared/hooks/usePopupState";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation/Goto";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { formatByteSize } from "@/shared/text";
import { formatNanoTimestamp } from "@/shared/time";

import { useSessionOutletContext } from "./useSessionOutletContext";

const columnHelper = createColumnHelper<SessionFile>();

function formatRangeBound(
  params: ValueFormatterParams<SessionFile, bigint | null>,
): string {
  // An absent bound means the contribution covers the whole file; render
  // a dash so the column reads as "open" rather than missing data. AG
  // Grid widens `value` to `TValue | null | undefined`, so collapse both.
  if (params.value === null || params.value === undefined) {
    return "—";
  }
  return formatNanoTimestamp(params.value);
}

// Mirrors the /files page's column set where the fields overlap; the
// range columns are session-specific. All values arrive on the row
// itself — the listing endpoint is a view model joined server-side.
const COLUMNS: ColDef<SessionFile>[] = [
  columnHelper.accessor("file_id", {
    headerName: "File ID",
    flex: 2,
    cellRenderer: (p: ICellRendererParams<SessionFile, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Files}/${id}`}
          className={cellStyles.idCell}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    cellRenderer: (p: ICellRendererParams<SessionFile, string | null>) => {
      const row = p.data;
      if (!row) {
        return null;
      }
      // The id is the label of last resort (a response predating the view
      // model); the link target is valid either way.
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Files}/${row.file_id}`}
          className={cellStyles.primaryContent}
          title={row.relative_path ?? undefined}
        >
          {p.value ?? row.file_id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    width: 160,
    variant: "relative",
  }),
  columnHelper.accessor("dataset_id", {
    headerName: "Dataset ID",
    width: 200,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<SessionFile, string | null>) => {
      // Session files live in the filesystem world, where a file's
      // association is always its dataset; the wire row carries no
      // association_type to gate on, so the link is unconditional.
      const datasetId = p.value;
      if (!datasetId) {
        return <EmptyCell />;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Datasets}/${datasetId}`}
          className={cellStyles.idCell}
        >
          {datasetId}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 2,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<SessionFile, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
  columnHelper.accessor("range_min_timestamp_ns", {
    headerName: "Range start",
    flex: 1,
    cellClass: cellStyles.monoCell,
    valueFormatter: formatRangeBound,
  }),
  columnHelper.accessor("range_max_timestamp_ns", {
    headerName: "Range end",
    flex: 1,
    cellClass: cellStyles.monoCell,
    valueFormatter: formatRangeBound,
  }),
  // The remaining columns are omitted from DEFAULT_VISIBLE_IDS below, so
  // they start hidden and are reachable through the column picker.
  {
    colId: "source",
    field: "origination",
    headerName: "Source",
    width: 200,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<SessionFile, string | null>) => {
      // Origination is free text written by uploader clients ("invocation:
      // iv_…", "roboto client vX"), not a parseable id, so it renders as
      // plain text with no link affordance.
      const origination = p.value ?? "";
      if (!origination) {
        return <EmptyCell />;
      }
      return origination;
    },
  },
  columnHelper.accessor("size", {
    headerName: "Size",
    width: 100,
    valueFormatter: (p) =>
      p.value === null || p.value === undefined ? "" : formatByteSize(p.value),
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    width: 160,
    variant: "relative",
  }),
  columnHelper.accessor("relative_path", {
    headerName: "Path",
    flex: 2,
    cellClass: "truncate",
  }),
];

const DEFAULT_VISIBLE_IDS = [
  "file_id",
  "name",
  "created",
  "dataset_id",
  "tags",
  "range_min_timestamp_ns",
  "range_max_timestamp_ns",
];

const AVAILABLE_COLUMNS: ColumnPickerOption[] = COLUMNS.map((c) => ({
  id: colId(c),
  label: c.headerName ?? colId(c),
}));

const PREFERENCE_DRIVEN_COLUMNS = buildColumnsForPreferences<SessionFile>({
  staticColumns: COLUMNS,
  dynamicColumns: [],
  preferenceOrder: DEFAULT_VISIBLE_IDS,
});

/**
 * Sources tab of the session-detail page. Lists the per-file
 * contributions to this session, paged through the table footer, with no
 * filters or sort. Each row is a server-joined view of the contribution
 * and its file's display fields, and rows are selectable for bulk
 * actions (visualize, download, add to collection).
 */
export function SourcesTab() {
  const { session } = useSessionOutletContext();
  const { goto } = useNavigation();
  const { mutateAsync: createExportJob } = useCreateExportJobMutation();
  const { beginExportJob } = useFileTransfer();
  const addToCollectionPopup = usePopupState<Set<string>>();
  // `usePopupState` rebuilds its container object every render; the stable
  // `open` callback is all the actions need, and depending on it keeps the
  // `selectionOptions` memo effective.
  const openAddToCollectionPopup = addToCollectionPopup.open;
  const [bulkActionError, setBulkActionError] =
    React.useState<React.ReactNode>(null);

  const table = useTable<SessionFile>({
    getRowId: (row) => row.file_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  // Each table page is one server page (`limit` = the footer's page
  // size), chained by token: page N's request carries the token page N-1
  // returned. The footer only steps one page at a time, so a page's
  // token is always recorded before the page is reachable.
  const [pageTokens, setPageTokens] = React.useState<(string | undefined)[]>(
    [],
  );
  const query = useSessionFiles({
    sessionId: session.session_id,
    pageToken: pageTokens[table.currentPage],
    limit: table.pageSize,
  });

  const { currentPage, pageSize } = table;
  const nextToken = query.data?.next_token;

  // A different page size re-chunks the listing — and another session is
  // a different listing — so recorded tokens no longer point at page
  // boundaries; `useTable` resets `currentPage` to 0 on the same pageSize
  // transition. Declared before `recordNextPageToken`: when the page size
  // toggles back to a value whose first page is still cached, both
  // effects fire on one commit, and the wipe must run first — a
  // recorded-then-wiped token would never be re-recorded, because
  // `recordNextPageToken`'s deps don't change on the following commit.
  React.useEffect(
    function resetTokenChain() {
      setPageTokens([]);
    },
    [pageSize, session.session_id],
  );

  React.useEffect(
    function recordNextPageToken() {
      if (!nextToken) {
        return;
      }
      setPageTokens((prev) => {
        if (prev[currentPage + 1] === nextToken) {
          return prev;
        }
        const next = [...prev];
        next[currentPage + 1] = nextToken;
        return next;
      });
    },
    [nextToken, currentPage],
  );

  const selectionOptions = React.useMemo<SelectionOption<SessionFile>[]>(
    () => [
      {
        name: "Visualize",
        icon: <QueryStatsIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { files: selected.map((file) => file.file_id) },
          });
        },
      },
      {
        name: "Download",
        icon: <DownloadIcon fontSize="small" />,
        action: async ({ selected }) => {
          setBulkActionError(null);
          const label =
            selected.length === 1
              ? selected[0].name ?? selected[0].file_id
              : `${selected.length} files`;
          try {
            const result = await createExportJob({
              nodeIds: selected.map((file) => file.file_id),
            });
            beginExportJob(result.export_job_id, label);
          } catch (error) {
            ErrorMonitoringService.captureError(error);
            setBulkActionError(
              userFacingErrorMessage(
                error,
                "Failed to start the download. Please try again.",
              ),
            );
          }
        },
      },
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected }) => {
          openAddToCollectionPopup({
            metadata: new Set(selected.map((file) => file.file_id)),
          });
        },
      },
    ],
    [openAddToCollectionPopup, beginExportJob, createExportJob, goto],
  );

  if (query.isError) {
    return (
      <Alert severity="error">
        {userFacingErrorMessage(
          query.error,
          <>
            Failed to load files. Please contact{" "}
            <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
            problem persists.
          </>,
        )}
      </Alert>
    );
  }

  return (
    <>
      <ResourceView.Root>
        {bulkActionError && (
          <Alert severity="error" onClose={() => setBulkActionError(null)}>
            {bulkActionError}
          </Alert>
        )}
        <ResourceView.Actions>
          <ColumnVisibility
            options={AVAILABLE_COLUMNS}
            visibleColumnIds={table.visibleColumnIds}
            setColumnVisibility={table.setColumnVisibility}
          />
          <ResourceView.Selection<SessionFile>
            selection={table.selection}
            clearSelection={table.clearSelection}
            options={selectionOptions}
          />
        </ResourceView.Actions>
        <DataTable<SessionFile>
          table={table}
          columns={PREFERENCE_DRIVEN_COLUMNS}
          data={query.data?.items ?? []}
          hasNextPage={Boolean(query.data?.next_token)}
          loading={deriveLoadingState(query)}
          noRowsContent="No files contribute to this session yet"
          selectionEnabled
        />
      </ResourceView.Root>
      <Dialog
        open={addToCollectionPopup.isOpen}
        onClose={addToCollectionPopup.close}
      >
        <AddToCollection
          selected={{
            type: "File",
            items: addToCollectionPopup.metadata ?? new Set(),
          }}
          onCancel={addToCollectionPopup.close}
        />
      </Dialog>
    </>
  );
}
